import React, { useState, useMemo, useEffect } from 'react';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import type { StatCardData } from '../types';
import { fetchSubscriptions, activateSubscription, deactivateSubscription, type AdminSubscription } from '../lib/subscriptionService';
import { logger } from '../lib/logger';

const statusClasses: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700 border border-blue-200',
  past_due: 'bg-red-50 text-red-700 border border-red-200',
  cancelled: 'bg-gray-100 text-gray-500 border border-gray-200',
  expired: 'bg-gray-100 text-gray-500 border border-gray-200',
};

const statusLabels: Record<string, string> = {
  active: 'Actif',
  past_due: 'Impayé',
  cancelled: 'Résilié',
  expired: 'Expiré',
};

const Subscriptions: React.FC = () => {
  const [subs, setSubs] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    fetchSubscriptions().then(setSubs).catch((err) => logger.error('load_subscriptions', 'Subscriptions', { error: String(err) })).finally(() => setLoading(false));
  }, []);

  const activeSubs = subs.filter((s) => s.status === 'active');
  const mrr = activeSubs.reduce((sum, s) => sum + (s.amountHt || 0), 0);

  const subStats: StatCardData[] = [
    { label: 'Total abonnements', value: String(subs.length), icon: '💳', color: 'blue', subtitle: `dont ${activeSubs.length} actifs` },
    { label: 'MRR', value: `${mrr.toLocaleString('fr-FR')} €`, icon: '💰', color: 'green', subtitle: 'revenu mensuel récurrent' },
    { label: 'Impayés', value: String(subs.filter(s => s.status === 'past_due').length), icon: '⚠', color: 'red', subtitle: 'à relancer' },
  ];

  const filtered = useMemo(() => {
    return subs.filter((s) => {
      const matchSearch = search === '' || s.companyName.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || s.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [subs, search, statusFilter]);

  const handleActivate = async (s: AdminSubscription) => {
    setActionLoading(s.id);
    try {
      await activateSubscription(s.employerId);
      alert('Abonnement activé manuellement.');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (s: AdminSubscription) => {
    setActionLoading(s.id);
    try {
      await deactivateSubscription(s.employerId);
      alert('Abonnement désactivé.');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const columns: Column<AdminSubscription>[] = [
    {
      key: 'company',
      header: 'Entreprise',
      render: (s) => <span className="font-medium text-gray-900">{s.companyName || '—'}</span>,
    },
    {
      key: 'amount',
      header: 'Montant',
      render: (s) => <span className="font-semibold text-gray-900">{s.amountHt || 0} €<span className="text-gray-400 font-normal">/mois</span></span>,
    },
    {
      key: 'status',
      header: 'Statut',
      render: (s) => (
        <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${statusClasses[s.status] || 'bg-gray-100 text-gray-500'}`}>
          {statusLabels[s.status] || s.status}
        </span>
      ),
    },
    {
      key: 'period',
      header: 'Période',
      render: (s) => (
        <div className="text-xs text-gray-500">
          <p>{s.currentPeriodStart || '—'}</p>
          <p>→ {s.currentPeriodEnd || '—'}</p>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Créé le',
      render: (s) => <span className="text-gray-500 text-xs">{s.createdAt}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (s) => (
        <div className="flex items-center gap-2">
          {s.status !== 'active' && (
            <button
              onClick={() => handleActivate(s)}
              disabled={actionLoading === s.id}
              className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-40"
            >
              Activer
            </button>
          )}
          {s.status === 'active' && (
            <button
              onClick={() => handleDeactivate(s)}
              disabled={actionLoading === s.id}
              className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-40"
            >
              Résilier
            </button>
          )}
        </div>
      ),
    },
  ];

  const filterSlot = (
    <select
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
    >
      <option value="all">Tous les statuts</option>
      <option value="active">Actif</option>
      <option value="past_due">Impayé</option>
      <option value="cancelled">Résilié</option>
    </select>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {subStats.map((stat, i) => (
          <StatCard key={i} data={stat} />
        ))}
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        keyExtractor={(s) => s.id}
        searchable
        searchPlaceholder="Rechercher par entreprise..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={10}
      />
    </div>
  );
};

export default Subscriptions;
